import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Edit, Plus, Search, ShieldOff, Trash2, Unlock, XCircle } from "lucide-react";
import {
  ativarUsuarioDistribuidor,
  desativarUsuarioDistribuidor,
  excluirUsuarioDistribuidor,
  listarUsuariosDistribuidorAdmin,
  obterDistribuidor,
} from "../services/api";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const DistribuidorUsuarios = ({ token }) => {
  const { showError, showSuccess, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const distribuidorId = searchParams.get("distribuidorId") || "";
  const [nome, setNome] = useState(distribuidorId ? "" : "Todas");
  const [usuarios, setUsuarios] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await listarUsuariosDistribuidorAdmin(token, distribuidorId ? { distribuidorId } : {});
      setUsuarios(Array.isArray(res.data) ? res.data : []);
      if (distribuidorId) {
        const d = await obterDistribuidor(distribuidorId, token);
        const item = d.data?.distribuidor || d.data;
        setNome(item?.nomeFantasia || item?.razaoSocial || "");
      }
    } catch (e) {
      setUsuarios([]);
      showError(e, "Erro ao listar usuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [token, distribuidorId]);

  const filtrados = useMemo(() => {
    const termo = busca.toLowerCase();
    if (!termo) return usuarios;
    return usuarios.filter((u) => (u.nome || "").toLowerCase().includes(termo) || (u.email || "").toLowerCase().includes(termo));
  }, [usuarios, busca]);

  const query = distribuidorId ? `?distribuidorId=${distribuidorId}` : "";

  if (loading) return <div className="p-6">Carregando usuários...</div>;

  return (
    <>
      <FeedbackAlert />
      <div className="max-w-6xl mx-auto zen-card rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold dark:text-white">
              Usuários do Distribuidor {distribuidorId ? `#${distribuidorId}` : ""} {nome ? `- ${nome}` : ""}
            </h2>
            <p className="text-xs text-gray-500 mt-1">Gerencie usuários de acesso do painel do distribuidor.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar usuário..." className="pl-10 pr-4 py-2 w-64 rounded-xl border dark:bg-slate-950/40 dark:text-white" />
            </div>
            <button className="inline-flex items-center px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold" onClick={() => navigate(`/distribuidores/usuarios/novo${query}`)}>
              <Plus size={18} className="mr-2" /> Novo Usuário
            </button>
          </div>
        </div>
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Nome</th>
              <th className="text-center py-3 px-4">Ativo</th>
              <th className="text-center py-3 px-4">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((u) => (
              <tr key={u.id} className="border-t border-gray-200 dark:border-white/10">
                <td className="py-3 px-4">{u.email}</td>
                <td className="py-3 px-4">{u.nome}</td>
                <td className="text-center py-3 px-4">{u.ativo ? <CheckCircle className="inline text-green-600" size={20} /> : <XCircle className="inline text-red-600" size={20} />}</td>
                <td className="text-center py-3 px-4">
                  <div className="inline-flex gap-2">
                    <button title="Editar" onClick={() => navigate(`/distribuidores/usuarios/editar/${u.id}${query}`)}><Edit size={18} /></button>
                    <button
                      title={u.ativo ? "Desativar" : "Ativar"}
                      onClick={async () => {
                        try {
                          if (u.ativo) await desativarUsuarioDistribuidor(u.distribuidorId, u.id, token);
                          else await ativarUsuarioDistribuidor(u.distribuidorId, u.id, token);
                          await carregar();
                        } catch (e) {
                          showError(e, "Erro ao alterar status.");
                        }
                      }}
                    >
                      {u.ativo ? <ShieldOff size={18} /> : <Unlock size={18} />}
                    </button>
                    <button
                      title="Excluir"
                      onClick={async () => {
                        if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;
                        try {
                          await excluirUsuarioDistribuidor(u.distribuidorId, u.id, token);
                          showSuccess("Usuário excluído com sucesso!");
                          await carregar();
                        } catch (e) {
                          showError(e, "Erro ao excluir usuário.");
                        }
                      }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtrados.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-gray-500">Nenhum usuário encontrado.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DistribuidorUsuarios;
